import { Geo } from '../../../utils/apollo/graphqlSchema';
import { assertCondition } from '../../../utils/types';
import { freeze } from '@reduxjs/toolkit';
import { UnreachableCaseError } from 'ts-essentials';

const stateNeighbors: Record<string, string[]> = freeze({
  AL: ['FL', 'GA', 'MS', 'TN'],
  // modify to avoid color conflict with HI
  AK: ['HI', 'CA', 'AZ'],
  AZ: ['CA', 'NM', 'NV', 'UT'],
  AR: ['LA', 'MO', 'MS', 'OK', 'TN', 'TX'],
  // modify to avoid color conflict with HI
  CA: ['AZ', 'NV', 'OR', 'HI'],
  CO: ['KS', 'NE', 'NM', 'OK', 'UT', 'WY'],
  CT: ['MA', 'NY', 'RI'],
  DE: ['MD', 'NJ', 'PA'],
  FL: ['AL', 'GA'],
  GA: ['AL', 'FL', 'NC', 'SC', 'TN'],
  // modify to avoid color conflict with AK and CA
  HI: ['AK', 'AZ', 'NM', 'TX'],
  ID: ['MT', 'NV', 'OR', 'UT', 'WA', 'WY'],
  IL: ['IA', 'IN', 'KY', 'MO', 'WI'],
  IN: ['IL', 'KY', 'MI', 'OH'],
  IA: ['IL', 'MN', 'MO', 'NE', 'SD', 'WI'],
  KS: ['CO', 'MO', 'NE', 'OK'],
  KY: ['IL', 'IN', 'MO', 'OH', 'TN', 'VA', 'WV'],
  LA: ['AR', 'MS', 'TX'],
  ME: ['NH'],
  MD: ['DE', 'PA', 'VA', 'WV'],
  MA: ['CT', 'NH', 'NY', 'RI', 'VT'],
  MI: ['IN', 'OH', 'WI'],
  MN: ['IA', 'ND', 'SD', 'WI'],
  MS: ['AL', 'AR', 'LA', 'TN'],
  MO: ['AR', 'IA', 'IL', 'KS', 'KY', 'NE', 'OK', 'TN'],
  MT: ['ID', 'ND', 'SD', 'WY'],
  NE: ['CO', 'IA', 'KS', 'MO', 'SD', 'WY'],
  NV: ['AZ', 'CA', 'ID', 'OR', 'UT'],
  NH: ['MA', 'ME', 'VT'],
  NJ: ['DE', 'NY', 'PA'],
  NM: ['AZ', 'CO', 'OK', 'TX', 'UT'],
  NY: ['CT', 'MA', 'NJ', 'PA', 'VT'],
  NC: ['GA', 'SC', 'TN', 'VA'],
  ND: ['MN', 'MT', 'SD'],
  OH: ['IN', 'KY', 'MI', 'PA', 'WV'],
  OK: ['AR', 'CO', 'KS', 'MO', 'NM', 'TX'],
  OR: ['CA', 'ID', 'NV', 'WA'],
  PA: ['DE', 'MD', 'NJ', 'NY', 'OH', 'WV'],
  RI: ['CT', 'MA'],
  SC: ['GA', 'NC'],
  SD: ['IA', 'MN', 'MT', 'ND', 'NE', 'WY'],
  TN: ['AL', 'AR', 'GA', 'KY', 'MO', 'MS', 'NC', 'VA'],
  TX: ['AR', 'LA', 'NM', 'OK'],
  UT: ['AZ', 'CO', 'ID', 'NM', 'NV', 'WY'],
  VT: ['MA', 'NH', 'NY'],
  VA: ['KY', 'MD', 'NC', 'TN', 'WV'],
  WA: ['ID', 'OR'],
  WV: ['KY', 'MD', 'OH', 'PA', 'VA'],
  WI: ['IA', 'IL', 'MI', 'MN'],
  WY: ['CO', 'ID', 'MT', 'NE', 'SD', 'UT'],
});

const stateAbbrList = Object.keys(stateNeighbors);

export const colorPattern = freeze([
  '#1f77b4',
  '#ff7f0e',
  '#2ca02c',
  '#d62728',
  '#9467bd',
  '#8c564b',
  '#e377c2',
  '#7f7f7f',
  '#bcbd22',
  '#17becf',
]);

// https://colordesigner.io/gradient-generator
export const gradientColorPattern = freeze([
  '#0076eb',
  '#1c85ed',
  '#3994ef',
  '#55a4f2',
  '#71b3f4',
  '#8ec2f6',
  '#aad1f8',
  '#c6e1fb',
  '#e3f0fd',
  '#ffffff',
]);

export const censusRegionsColors = freeze({
  West: colorPattern[0],
  Midwest: colorPattern[1],
  South: colorPattern[2],
  Northeast: colorPattern[3],
});

export const censusDivisionColors = freeze({
  Pacific: colorPattern[0],
  Mountain: colorPattern[1],
  'West South Central': colorPattern[2],
  'West North Central': colorPattern[3],
  'South Atlantic': colorPattern[4],
  'East South Central': colorPattern[5],
  'East North Central': colorPattern[6],
  'Middle Atlantic': colorPattern[7],
  'New England': colorPattern[8],
});

// generate state colors and avoid color conflict with neighbors
const generateStateColors = () => {
  const stateColors: any = {};
  const usedColors = new Set();

  const getAvailableColor = (state: any, neighbors: any) => {
    const neighborColors = new Set(
      neighbors.map((n: any) => stateColors[n]).filter(Boolean),
    );
    for (let color of colorPattern) {
      if (!neighborColors.has(color)) {
        return color;
      }
    }
    return null;
  };

  // Sort states by number of neighbors, descending
  const sortedStates = stateAbbrList.sort((stateA, stateB) => {
    return (
      (stateNeighbors[stateB]?.length ?? 0) -
      (stateNeighbors[stateA]?.length ?? 0)
    );
  });

  for (let state of sortedStates) {
    if (!stateColors[state]) {
      const color = getAvailableColor(state, (stateNeighbors as any)[state]);
      if (color) {
        stateColors[state] = color;
        usedColors.add(color);
      } else {
        // If no color is available, use the least used color
        const leastUsedColor = colorPattern.reduce((a, b) =>
          (usedColors.has(a) ? usedColors.size : Infinity) <
          (usedColors.has(b) ? usedColors.size : Infinity)
            ? a
            : b,
        );
        stateColors[state] = leastUsedColor;
        usedColors.add(leastUsedColor);
      }
    }
  }

  return stateColors as any;
};

export const getGradientColor = (
  value: number | null,
  largestValue: number | null,
) => {
  if (!value || !largestValue) {
    return '#fff';
  }
  const index = Math.round(
    (value / largestValue) * (gradientColorPattern.length - 1),
  );
  return gradientColorPattern[gradientColorPattern.length - 1 - index];
};

const stateColorMap = generateStateColors();

// non-heatMap color dictionary, key is the field value in ICountyMapProperties, value is the color
export const getMapColorDictionary = (geo: Geo) => {
  assertCondition(geo !== Geo.Na);
  switch (geo) {
    case Geo.UsState:
      return stateColorMap;
    case Geo.UsCensusRegion:
      return censusRegionsColors;
    case Geo.UsDma:
      return colorPattern;
    case Geo.UsCensusDivision:
      return censusDivisionColors;
    default:
      throw new UnreachableCaseError(geo);
  }
};
